import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BaseModal from './common/BaseModal';
import { useAuth } from '../contexts/AuthContext';
import { useFormattedNumber } from '../hooks/useFormattedNumber';

const CloneScenarioModal = ({ show, onHide, scenarioData }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Mortgage details state
  const priceFormat = useFormattedNumber('');
  const closingCostsFormat = useFormattedNumber('');
  const repairEstimatesFormat = useFormattedNumber('');
  const homeValueFormat = useFormattedNumber('');
  const monthlyRentFormat = useFormattedNumber('');
  const [downPaymentPercent, setDownPaymentPercent] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [term, setTerm] = useState('30');
  const [isInterestOnly, setIsInterestOnly] = useState(false);
  const [isCashPurchase, setIsCashPurchase] = useState(false);
  const [cashToClose, setCashToClose] = useState(0);

  // Calculate cash to close whenever relevant values change
  useEffect(() => {
    if (priceFormat.value || closingCostsFormat.value || repairEstimatesFormat.value) {
      const downPaymentAmount = Number(priceFormat.value) * (Number(downPaymentPercent) / 100);
      const totalCash = downPaymentAmount + 
                       Number(closingCostsFormat.value) + 
                       Number(repairEstimatesFormat.value);
      setCashToClose(totalCash);
    }
  }, [priceFormat.value, downPaymentPercent, closingCostsFormat.value, repairEstimatesFormat.value]);

  useEffect(() => {
    if (show) {
      setName(`Copy of ${scenarioData?.name || ''}`);
      
      // Initialize mortgage details with current values
      priceFormat.onChange({ target: { value: scenarioData?.price?.toString() || '' } });
      closingCostsFormat.onChange({ target: { value: scenarioData?.closingCosts?.toString() || '' } });
      repairEstimatesFormat.onChange({ target: { value: scenarioData?.repairEstimates?.toString() || '' } });
      homeValueFormat.onChange({ target: { value: scenarioData?.initialHomeValue?.toString() || '' } });
      monthlyRentFormat.onChange({ target: { value: scenarioData?.monthlyRent?.toString() || '' } });
      setDownPaymentPercent(scenarioData?.downPaymentPercent?.toString() || '');
      setInterestRate(scenarioData?.interestRate?.toString() || '');
      setTerm(scenarioData?.loanTermYears?.toString() || '30');
      setIsInterestOnly(scenarioData?.isInterestOnly || false);
      setIsCashPurchase(scenarioData?.isCashPurchase || false);
    }
  }, [show, scenarioData]);

  const [validated, setValidated] = useState(false);
  const [nameError, setNameError] = useState('');

  const validateForm = () => {
    if (!name.trim()) {
      setNameError('Scenario name is required');
      return false;
    }
    
    // Validate mortgage details
    if (!isCashPurchase) {
      if (!downPaymentPercent || parseFloat(downPaymentPercent) <= 0 || parseFloat(downPaymentPercent) > 100) {
        setNameError('Down payment must be between 0 and 100%');
        return false;
      }
      
      if (!interestRate || parseFloat(interestRate) <= 0) {
        setNameError('Interest rate must be greater than 0%');
        return false;
      }
      
      if (!term || parseInt(term) <= 0) {
        setNameError('Loan term must be greater than 0 years');
        return false;
      }
    }
    
    setNameError('');
    return true;
  };

  const handleCreateClone = async (e) => {
    if (e) e.preventDefault();
    
    setValidated(true);
    if (!validateForm()) return;
    
    setIsSaving(true);
    try {
      // Get values for calculations
      const price = parseFloat(priceFormat.value) || scenarioData.price || 0;
      const downPaymentPercentValue = isCashPurchase ? 100 : (parseFloat(downPaymentPercent) || scenarioData.downPaymentPercent || 0);
      const interestRateValue = isCashPurchase ? 0 : (parseFloat(interestRate) || scenarioData.interestRate || 0);
      const termValue = isCashPurchase ? 0 : (parseInt(term) || scenarioData.loanTermYears || 30);
      const closingCostsValue = parseFloat(closingCostsFormat.value) || scenarioData.closingCosts || 0;
      const repairEstimatesValue = parseFloat(repairEstimatesFormat.value) || scenarioData.repairEstimates || 0;
      const monthlyRentValue = parseFloat(monthlyRentFormat.value) || scenarioData.monthlyRent || 0;
      const initialHomeValueValue = parseFloat(homeValueFormat.value) || scenarioData.initialHomeValue || 0;
      const isInterestOnlyValue = !isCashPurchase && isInterestOnly;
      
      // Calculate monthly mortgage payment
      const monthlyMortgage = (() => {
        if (isCashPurchase) return 0;
        
        const principal = price * (1 - downPaymentPercentValue / 100);
        const monthlyRate = (interestRateValue / 100) / 12;
        const numberOfPayments = termValue * 12;
        
        if (isInterestOnlyValue) {
          // For interest-only loans, payment is just the monthly interest
          return principal * monthlyRate;
        } else {
          // For amortizing loans, use the standard formula
          const payment = principal * 
            (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
            (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
          
          return isNaN(payment) ? 0 : payment;
        }
      })();
      
      // Calculate yearly values
      const yearlyGrossRent = monthlyRentValue * 12;
      const yearlyMortgage = monthlyMortgage * 12;
      
      // Get other expense values from the original scenario
      const yearlyInsurance = (scenarioData.monthlyInsurance || 0) * 12;
      const yearlyTaxes = scenarioData.propertyTax || 0;
      const managementFeePercent = scenarioData.managementFee || 0;
      const vacancyRatePercent = scenarioData.vacancyRate || 0;
      const maintenanceRatePercent = scenarioData.maintenanceRate || 0;
      
      const yearlyManagement = yearlyGrossRent * (managementFeePercent / 100);
      const yearlyVacancy = yearlyGrossRent * (vacancyRatePercent / 100);
      const yearlyMaintenance = yearlyGrossRent * (maintenanceRatePercent / 100);
      
      const totalExpenses = Number((yearlyInsurance + yearlyTaxes + yearlyManagement + yearlyVacancy + yearlyMaintenance).toFixed(2));
      const noi = yearlyGrossRent - totalExpenses;
      const cashFlow = noi - yearlyMortgage;
      
      // Calculate investment metrics
      const totalCashInvested = (price * (downPaymentPercentValue / 100)) + closingCostsValue + repairEstimatesValue;
      const cashFlowPercent = totalCashInvested > 0 ? (cashFlow / totalCashInvested) * 100 : 0;
      
      const initialValue = initialHomeValueValue || price || 0;
      const capRate = initialValue > 0 ? (noi / initialValue) * 100 : 0;
      
      // Calculate appreciation
      const appreciationRate = (scenarioData.appreciation || 0) / 100;
      const currentYearValue = initialValue * (1 + appreciationRate);
      const yearlyAppreciationAmount = currentYearValue - initialValue;
      const appreciationPercent = totalCashInvested > 0 ? (yearlyAppreciationAmount / totalCashInvested) * 100 : 0;
      
      // Calculate principal paydown
      let yearlyPrincipalPaydown = 0;
      let principalPaydownPercent = 0;
      
      if (!isInterestOnlyValue && !isCashPurchase) {
        // Only calculate principal paydown for regular amortizing loans
        let remainingBalance = price * (1 - downPaymentPercentValue / 100);
        const startOfYearBalance = remainingBalance;
        const monthlyRate = (interestRateValue / 100) / 12;
        
        // Calculate first year's principal paydown
        for (let month = 1; month <= 12; month++) {
          const interestPayment = remainingBalance * monthlyRate;
          const principalPayment = monthlyMortgage - interestPayment;
          remainingBalance = remainingBalance - principalPayment;
        }
        
        yearlyPrincipalPaydown = startOfYearBalance - remainingBalance;
        principalPaydownPercent = totalCashInvested > 0 ? (yearlyPrincipalPaydown / totalCashInvested) * 100 : 0;
      }
      
      // Calculate total ROI
      const totalROI = appreciationPercent + cashFlowPercent + principalPaydownPercent;
      
      // Create new scenario data with updated mortgage details and calculated metrics
      const newScenarioData = {
        ...scenarioData,
        name,
        // Update mortgage details
        price: price,
        downPaymentPercent: downPaymentPercentValue,
        interestRate: interestRateValue,
        loanTermYears: termValue,
        isInterestOnly: isInterestOnlyValue,
        isCashPurchase: isCashPurchase,
        closingCosts: closingCostsValue,
        repairEstimates: repairEstimatesValue,
        initialHomeValue: initialHomeValueValue,
        monthlyRent: monthlyRentValue,
        // Add calculated metrics
        cashToClose: totalCashInvested,
        calculations: {
          // Metrics
          capRate: capRate || 0,
          annualCashFlow: cashFlow || 0,
          annualCashOnCashReturn: cashFlowPercent || 0,
          dscr: 0,
          annualROI: totalROI || 0,
          
          // Annual values
          annualGrossRent: yearlyGrossRent || 0,
          annualExpenses: 0,
          annualInsurance: 0,
          annualMaintenance: 0,
          annualManagement: 0,
          annualMortgage: yearlyMortgage * 12 || 0,
          annualTaxes: 0,
          annualVacancy: 0
        },
        // Metadata
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userId: currentUser.uid // Stamp userId for tracking and ownership
      };
      const docRef = await addDoc(collection(db, 'scenarios'), newScenarioData);
      onHide();
      navigate(`/scenario/${docRef.id}`);
    } catch (error) {
      console.error("Error creating clone:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const modalFooter = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onHide}
        disabled={isSaving}
      >
        Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleCreateClone}
        disabled={isSaving || !name.trim()}
      >
        {isSaving ? 'Creating...' : 'Create Clone'}
      </button>
    </>
  );

  if (!show) return null;

  return (
    <BaseModal
      isOpen={show}
      onClose={onHide}
      title="Clone Scenario"
      footer={modalFooter}
      maxWidth="900px"
      width="70%"
    >
      <style>
        {`
          .form-check-input:checked {
            background-color: #0d6efd;
            border-color: #0d6efd;
          }
        `}
      </style>
      <form className="needs-validation" noValidate onSubmit={handleCreateClone}>
        <div className="mb-3">
          <label className="form-label text-white">
            Scenario Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control bg-dark text-light border-secondary ${validated && !name.trim() ? 'is-invalid' : ''}`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (validated) validateForm();
            }}
            placeholder="Enter scenario name"
            required
          />
        </div>
                
        <div className="row g-3">
          {/* Purchase Price - Full Width */}
          <div className="col-12">
            <label className="form-label text-white">Purchase Price</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="text"
                className="form-control bg-dark text-light border-secondary"
                value={priceFormat.formatted}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^\d.]/g, '');
                  priceFormat.onChange({ target: { value: rawValue } });
                }}
                placeholder="Enter price"
              />
            </div>
          </div>
          
          {/* Toggles */}
          <div className="col-12">
            <div className="d-flex">
              <div className="form-check form-switch mb-2 me-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="cashPurchaseToggle"
                  checked={isCashPurchase}
                  onChange={() => {
                    setIsCashPurchase(!isCashPurchase);
                    if (!isCashPurchase) {
                      // When enabling cash purchase, set down payment to 100% and disable interest-only
                      setDownPaymentPercent('100');
                      setIsInterestOnly(false);
                    }
                  }}
                  disabled={isInterestOnly}
                />
                <label className="form-check-label text-muted" htmlFor="cashPurchaseToggle">
                  Cash Purchase
                </label>
              </div>
              <div className="form-check form-switch mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="interestOnlyToggle"
                  checked={isInterestOnly}
                  onChange={() => {
                    setIsInterestOnly(!isInterestOnly);
                    if (!isInterestOnly) {
                      // When enabling interest-only, disable cash purchase
                      setIsCashPurchase(false);
                    }
                  }}
                  disabled={isCashPurchase}
                />
                <label className="form-check-label text-muted" htmlFor="interestOnlyToggle">
                  Interest-Only Loan
                </label>
              </div>
            </div>
          </div>
          
          {/* Down Payment, Interest Rate, Term - One Row, Equal Width */}
          <div className="col-md-4">
            <label className="form-label text-white">Down Payment (%)</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control bg-dark text-light border-secondary"
                value={isCashPurchase ? '100' : downPaymentPercent}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d.]/g, '');
                  setDownPaymentPercent(value);
                }}
                placeholder="Enter down payment"
                disabled={isCashPurchase}
              />
              <span className="input-group-text">%</span>
            </div>
          </div>
          
          <div className="col-md-4">
            <label className="form-label text-white">Interest Rate (%)</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control bg-dark text-light border-secondary"
                value={isCashPurchase ? '0' : interestRate}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d.]/g, '');
                  setInterestRate(value);
                }}
                placeholder="Enter interest rate"
                disabled={isCashPurchase}
              />
              <span className="input-group-text">%</span>
            </div>
          </div>
          
          <div className="col-md-4">
            <label className="form-label text-white">Term (Years)</label>
            <input
              type="number"
              className="form-control bg-dark text-light border-secondary"
              value={isCashPurchase ? '0' : term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Enter loan term"
              min="1"
              max="30"
              disabled={isCashPurchase}
            />
          </div>
          
          {/* Closing Costs */}
          <div className="col-12 mb-3">
            <label className="form-label text-white">Closing Costs ($)</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="text"
                className="form-control bg-dark text-light border-secondary"
                value={closingCostsFormat.formatted}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^\d.]/g, '');
                  closingCostsFormat.onChange({ target: { value: rawValue } });
                }}
                placeholder="Enter closing costs"
              />
            </div>
          </div>
          
          {/* <h6 className="text-white mb-3">Valuation Details</h6> */}
            <div className="col-md-6">
              <label className="form-label text-white">Repair Estimates ($)</label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="text"
                  className="form-control bg-dark text-light border-secondary"
                  value={repairEstimatesFormat.formatted}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^\d.]/g, '');
                    repairEstimatesFormat.onChange({ target: { value: rawValue } });
                  }}
                  placeholder="Enter repair estimates"
                />
              </div>
            </div>
            
            <div className="col-md-6">
              <label className="form-label text-white">ARV ($)</label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="text"
                  className="form-control bg-dark text-light border-secondary"
                  value={homeValueFormat.formatted}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^\d.]/g, '');
                    homeValueFormat.onChange({ target: { value: rawValue } });
                  }}
                  placeholder="Enter after repair value"
                />
              </div>
            </div>
          
          {/* Monthly Rent - Full Width */}
          <div className="col-12 mb-3">
            <label className="form-label text-white">Monthly Rent ($)</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="text"
                className="form-control bg-dark text-light border-secondary"
                value={monthlyRentFormat.formatted}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^\d.]/g, '');
                  monthlyRentFormat.onChange({ target: { value: rawValue } });
                }}
                placeholder="Enter monthly rent"
              />
            </div>
          </div>
          
          {/* Total Cash to Close - Full Width */}
          <div className="col-12">
            <label className="form-label text-white">Total Cash to Close</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="text"
                className="form-control bg-dark text-light border-secondary"
                value={cashToClose.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                readOnly
                disabled
              />
            </div>
            <small className="text-muted">Down Payment + Closing Costs + Repair Estimates</small>
          </div>
        </div>
        
        {nameError && <div className="alert alert-danger mt-3">{nameError}</div>}
      </form>
    </BaseModal>
  );
};

export default CloneScenarioModal;